import { Clear } from '@mui/icons-material'
import { filterHeaderTableProps, getFilterHeaderStyles } from './FilterInfiniteScrollTable.action'
import { Box, IconButton, styled, Typography, useTheme } from '@mui/material'
import { GridActionsCellItem, GridColumns, GridNativeColTypes, GridRowId } from '@mui/x-data-grid-pro'
import debounce from 'lodash/debounce'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdArrowBack, MdCopyAll, MdDelete, MdEdit, MdSend } from 'react-icons/md'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateCertificateCache } from 'src/api/Certificates'
import { invalidateCustomer } from 'src/api/customer'
import { invalidateDeviation } from 'src/api/deviation'
import { invalidateDocument } from 'src/api/document'
import { invalidateLocalityCache, useGetCustomerLocalities, useGetLocality } from 'src/api/localities'
import {
  invalidateMainComponentCache,
  mainEndpoints,
  useAddMainComponentToLocality,
  useBringBackMainComponent,
  useDiscardEquipmentById,
  useUpdateMainComponentToOutgoing,
} from 'src/api/main-component'
import { invalidateMainComponentTypeCache } from 'src/api/main-component-type'
import { invalidatePositionCache } from 'src/api/positions'
import { useGetAllEligibleServiceStations } from 'src/api/service-station'
import { useGetAllStorageWithCustomers } from 'src/api/storage'
import { useGenerateExcelReports } from 'src/api/system/system-report'
import { invalidateDocumentCategory } from 'src/api/users/document-category'
import { ReactComponent as ExcelDownload } from 'src/assets/svg/ExcelDownload.svg'
import { hasPermission } from 'src/components/common/Authorization'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { BulkActionHeaderActions } from 'src/components/customer-admin/equipments/BulkActionHeaderActions'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import GenericTable from 'src/components/table/GenericTable'
import InfiniteScrollTable from 'src/components/table/InfiniteScrollTable'
import { NO_VALID_CERTIFICATE } from 'src/constant'
import { CustomerCtx } from 'src/context/CustomerContext'
import { DialogContextType, FacilityTypes, MainComponentStatus, MonthShortName, SystemReportTypes } from 'src/enums'
import { permissionValue } from 'src/enums/permissionValue'
import { IMainComponent, MainComponentFilters } from 'src/generated-types/main-component'
import { useCustomerCtx, useDialogCtx } from 'src/hooks/context-hooks'
import { useRowCount } from 'src/hooks/useViewportHeight'
import { MUIThemeColors } from 'src/mui-theme'
import { mainComponentQueryKeys } from 'src/query-keys'
import { CreateMainComponentDialog } from 'src/screens/customer/mainComponents/CreateMainComponentDialog'
import { UpdateMainComponentDialog } from 'src/screens/customer/mainComponents/UpdateMainComponentDialog'
import { EquipmentDetailModal } from 'src/screens/localities/mainComponents/EquipmentDetailModal'
import { StyledCard } from 'src/screens/localities/maintenance/EquipmentMaintenanceContainer'
import { MainComponentSendModalComponent } from 'src/screens/localities/positions/components/MainComponentSendModal'
import { SendToLocalityDialog } from 'src/screens/serviceStations/SendToLocalityDialog'
import { getThemeColor, getTranslatedText } from 'src/utils'
import { downloadFileLocal } from 'src/utils/downloadFile'
import {
  getCageName,
  formatMainComponentIdentification,
  getMainComponentStatus,
  validatePositionAndInstallation,
} from 'src/utils/function'
import NoEquipment from '../../assets/svg/no-items-common-image.svg'
import { getStatusOptions } from './EquipmentFiltersHelper'
import { FilterHeader } from './FilterHeader'
import FilterChips from './filters/FilterChips'

interface EquipmentListContainerProps {
  customerId: EntityId
  searchText?: string
  onHeaderFiltersChange: (filters: MainComponentFilters) => void
  headerFilters: MainComponentFilters
  onTotalCountChange?: (count: number) => void
}

const EquipmentListContainer: FC<EquipmentListContainerProps> = ({
  customerId,
  searchText,
  onHeaderFiltersChange,
  headerFilters,
  onTotalCountChange,
}) => {
  const theme = useTheme()
  const { localityId, mooringId, positionId } = useParams()

  const { t: translate, i18n } = useTranslation(['main-component', 'common', 'main-component-type'])
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const { permission } = useCustomerCtx()
  const rowCount = useRowCount()
  const { mutateAsync, isLoading: isDeleting, error: discardError } = useDiscardEquipmentById(customerId as EntityId)

  const [mainComponentId, setMainComponentId] = useState<EntityId | undefined>()
  const [selectionModel, setSelectionModel] = useState<GridRowId[]>([])
  const { mutateAsync: updateMainComponentToOutgoing } = useUpdateMainComponentToOutgoing(
    customerId as EntityId,
    mainComponentId as EntityId,
  )
  const [selectedPositionId, setSelectedPositionId] = useState<EntityId>(0)
  const { mutateAsync: addMainComponentToLocality } = useAddMainComponentToLocality(
    customerId as EntityId,
    selectedPositionId,
  )
  const { mutateAsync: bringBackMainComponent } = useBringBackMainComponent(customerId as EntityId)
  const { mutateAsync: generateExcel, isLoading: isGeneratingExcel } = useGenerateExcelReports()

  const [localFilters, setLocalFilters] = useState<Record<string, string>>({})

  const { data: localities, isLoading: localitiesLoading } = useGetCustomerLocalities({
    clientId: customerId as EntityId,
  })

  const { data: serviceStations, isLoading: serviceStationsLoading } = useGetAllEligibleServiceStations(
    customerId as EntityId,
  )
  const { data: storages, isLoading: storagesLoading } = useGetAllStorageWithCustomers(customerId as EntityId)

  const isLocationDataLoading = localitiesLoading || serviceStationsLoading || storagesLoading

  const filterInputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const tableDataRef = useRef<IMainComponent[]>([])

  const selectedRows = useMemo(
    () => tableDataRef.current.filter(row => selectionModel.includes(row.id) && !row.isDeleted),
    [selectionModel],
  )
  const { data: localityForBulk } = useGetLocality(
    customerId as EntityId,
    (selectedRows[0]?.selectedLocalityId ?? 0) as EntityId,
    { mainComponentId: selectedRows[0]?.id as EntityId },
  )

  const handleDataChange = useCallback((newData: IMainComponent[]) => {
    tableDataRef.current = newData
  }, [])

  const handleTotalCountChange = useCallback(
    (count: number) => {
      onTotalCountChange?.(count)
    },
    [onTotalCountChange],
  )

  const onUpdate = useCallback(
    async (row: IMainComponent) => {
      showDialog(
        UpdateMainComponentDialog,
        {
          opts: { maxWidth: 'md', fullWidth: true },
          componentProps: {
            id: row.id as EntityId,
            mainComponentTypeName: row.type.name,
            customerId: customerId as EntityId,
            searchText: searchText,
          },
        },
        undefined,
        true,
      )
    },
    [searchText, customerId],
  )

  const onClone = useCallback(
    async (row: IMainComponent) => {
      const { serialNumber, name, id, certificates, ...rest } = row
      showDialog(
        CreateMainComponentDialog,
        {
          opts: {
            maxWidth: 'md',
            fullWidth: true,
          },
          componentProps: {
            customerId: customerId as EntityId,
            initialValues: {
              componentTypeId: row.typeId,
              ...rest,
              positionId: null,
              certificateNumber: null,
              certificateSupplierId: null,
              certificateDocumentType: 1,
              certificateDate: null,
              certificateFileId: null,
              validUntil: null,
            },
            mainComponentTypeName: row.type.name,
            searchText: searchText,
            clonedFromEquipment: row as IMainComponent,
          },
        },
        DialogContextType.customerAdminContext,
        true,
      )
    },
    [customerId],
  )

  const onDeleteMainComponent = (mainComponent: IMainComponent) => {
    showConfirmDialog(
      translate('discardMainComponent', { ns: 'main-component' }),
      translate('areYouSureDiscardMainComponent', { ns: 'main-component' }),
      {
        acceptText: translate('yes', { ns: 'common' }),
        cancelText: translate('no', { ns: 'common' }),
      },
      async pop => {
        const localityId = mainComponent.selectedLocalityId
        await mutateAsync(
          { id: mainComponent.id, shouldNotifyClientAdmins: false },
          {
            onSuccess: () => {
              toast.success(translate('mainComponentDiscarded', { ns: 'main-component' }))
              invalidateMainComponentCache.getMainComponentsByComponentType(
                customerId,
                mainComponent.typeId,
                searchText,
              )
              invalidateMainComponentCache.getDiscardedMainComponents(customerId)
              invalidateMainComponentTypeCache.useGetMainComponentTypeWithMainComponentCount(customerId)
              if (localityId) invalidateMainComponentCache.invalidateEquipment(customerId, { localityId })
              invalidateMainComponentCache.getMaincomponentsBySearch(customerId, searchText)
              invalidateMainComponentCache.getMaincomponentsBySearchWithFilters(customerId as EntityId)
              pop()
            },
            onError: () => {
              pop()
              toast.error(translate('cannotDiscardActiveMainComponent', { ns: 'main-component' }))
            },
          },
        )
      },
    )
  }

  const onSend = useCallback(
    async (id: EntityId) => {
      showDialog(MainComponentSendModalComponent, {
        title: translate('sendToServiceStation', { ns: 'main-component' }),
        componentProps: {
          customerId,
          localityId,
          mooringId,
          positionId,
          id,
          searchText: searchText,
          onlyServiceStation: false,
        },
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
      })
    },
    [customerId, localityId, mooringId, positionId, searchText, showDialog, translate],
  )

  const bringBack = row => {
    showConfirmDialog(
      translate('bringBackMainComponent'),
      translate('areYouSureBringBackMainComponent'),
      { acceptText: translate('yes', { ns: 'common' }), cancelText: translate('no', { ns: 'common' }) },
      async pop => {
        await bringBackMainComponent({ id: row.id }).then(() => {
          toast(translate('mainComponentBringBack'), { type: 'success' })
          invalidateMainComponentCache.getDiscardedMainComponents(customerId)
          invalidateMainComponentTypeCache.useGetMainComponentTypeWithMainComponentCount(customerId)
          invalidateMainComponentCache.getMainComponentsByComponentType(customerId, row.typeId, searchText)
          invalidateMainComponentCache.getMaincomponentsBySearch(customerId, searchText)
          invalidateMainComponentCache.getMaincomponentsBySearchWithFilters(customerId)
          pop()
        })
      },
    )
  }

  const onSubmit = useCallback(
    async (form, pop, formContext) => {
      if (!validatePositionAndInstallation(form, translate, formContext)) return
      if (form.selectedPositionId != null) {
        setSelectedPositionId(form.selectedPositionId)
        await addMainComponentToLocality(form, {
          onSuccess: () => {
            invalidateMainComponentCache.getMainComponents(customerId, localityId, mooringId, selectedPositionId)
            invalidateMainComponentCache.getOutgoingMainComponents(customerId, localityId as EntityId)
            invalidateCertificateCache.getCertificatesByPosition(customerId, selectedPositionId)
            invalidatePositionCache.getCagePositionDeviations(customerId, localityId as EntityId, selectedPositionId)
            invalidateMainComponentCache.getMainComponentsByComponentType(
              customerId,
              form.mainComponentTypeId as EntityId,
              searchText,
            )
            invalidateMainComponentCache.getMainComponentById(customerId, form.mainComponentId ?? 0)
            invalidateMainComponentCache.getMaincomponentsBySearchWithFilters(customerId as EntityId)
            invalidateDeviation.invalidateAllDeviations(customerId as EntityId, localityId)
            invalidateCustomer.getCustomersActivityCounts(customerId)
            invalidationActivityLogCache.getCageActivityLogs()
            invalidateDocumentCategory.getDocumentCategoriesWithDocuments(customerId)
            invalidateDocument.getDocuments(customerId)
            invalidateDocument.getDocumentsForApproval(customerId, localityId)
            invalidateLocalityCache.getCageStatus(customerId, localityId)
            invalidateLocalityCache.getLocality(customerId, localityId as EntityId)
            invalidateDeviation.getDeviations(customerId)
            toast.success(translate('successfully-sent-to-locality', { ns: 'main-component' }))
            pop()
          },
          onError: error => {
            if (error && error['data'] == 'ExceedMaxCountForCage') {
              toast(translate('exceedMaxCountForCage', { ns: 'main-component' }), { type: 'error' })
            }
            if (error && error['data'] == NO_VALID_CERTIFICATE) {
              toast(translate('NoValidCertificate', { ns: 'service-station' }), { type: 'error' })
            }
            if (error['data'] == 'CertificateNumberExists') {
              toast(translate('certificateNumberExists', { ns: 'main-component' }), { type: 'error' })
              if (formContext && typeof formContext.setError === 'function') {
                formContext.setError('certificateNumber', {
                  type: 'manual',
                  message: translate('certificateNumberExists', { ns: 'main-component' }),
                })
              }
            }
          },
        })
      } else {
        await updateMainComponentToOutgoing(form, {
          onSuccess: () => {
            invalidateMainComponentCache.getMainComponentsByComponentType(
              customerId,
              form.mainComponentTypeId as EntityId,
              searchText,
            )
            invalidateMainComponentCache.getMaincomponentsBySearchWithFilters(customerId as EntityId)
            invalidateDeviation.invalidateAllDeviations(customerId as EntityId, localityId)
            invalidateDeviation.getAllDeviationsForScheduledWorkOrders(customerId as EntityId, localityId as EntityId)
            invalidateCustomer.getCustomersActivityCounts(customerId)
            invalidationActivityLogCache.getCageActivityLogs()
            invalidateDocument.getDocuments(customerId)
            invalidateDocument.getDocumentsForApproval(customerId, localityId)
            invalidateMainComponentCache.getMainComponentById(customerId, form.mainComponentId ?? 0)
            pop()
            toast.success(translate('successfully-sent-to-locality', { ns: 'main-component' }))
          },
          onError: error => {
            if (error && error['data'] == NO_VALID_CERTIFICATE) {
              pop()
              toast(translate('NoValidCertificate', { ns: 'service-station' }), { type: 'error' })
            }
            if (error['data'] == 'CertificateNumberExists') {
              toast(translate('certificateNumberExists', { ns: 'main-component' }), { type: 'error' })
              if (formContext && typeof formContext.setError === 'function') {
                formContext.setError('certificateNumber', {
                  type: 'manual',
                  message: translate('certificateNumberExists', { ns: 'main-component' }),
                })
              }
            }
          },
        })
      }
    },
    [customerId, localityId, mooringId, selectedPositionId, mainComponentId],
  )

  const onSendToOutgoing = useCallback(
    async (mainComponent: IMainComponent) => {
      setMainComponentId(mainComponent.id)
      showDialog(
        SendToLocalityDialog,
        {
          componentProps: {
            customerId,
            mainComponentId: mainComponent.id as EntityId,
            mainComponentTypeId: mainComponent.typeId as EntityId,
            onSubmit,
          },
          opts: {
            maxWidth: 'sm',
            fullWidth: true,
          },
        },
        undefined,
        true,
      )
    },
    [customerId, mainComponentId],
  )

  const renderMainComponentLocation = useCallback(params => {
    const mainComponent = params.row as IMainComponent
    if (mainComponent) {
      if (mainComponent.selectedPositionName) {
        return (
          <Box>
            {mainComponent.selectedLocalityName && (
              <DynamicTextTypography text={mainComponent.selectedLocalityName + '-'} maxLength={10} />
            )}
            <h6>{getCageName(mainComponent.selectedPositionName)}</h6>
          </Box>
        )
      } else if (mainComponent.selectedLocalityName) {
        return (
          <Box>
            <h5>{mainComponent.selectedLocalityName ?? '-'} </h5>
          </Box>
        )
      } else if (mainComponent.assignedServiceStationName) {
        return (
          <Box>
            <h5>{mainComponent.assignedServiceStationName} </h5>
          </Box>
        )
      } else {
        return (
          <Box>
            <h5>{mainComponent.storageName ?? '-'} </h5>
          </Box>
        )
      }
    }

    return <h5>{'-'}</h5>
  }, [])

  const debouncedServerFilter = useMemo(
    () =>
      debounce((filters: MainComponentFilters) => {
        onHeaderFiltersChange(filters)
      }, 500),
    [onHeaderFiltersChange],
  )

  useEffect(() => {
    return () => {
      debouncedServerFilter.cancel()
    }
  }, [debouncedServerFilter])

  useEffect(() => {
    const initialFilters: Record<string, string> = {}
    Object.keys(headerFilters).forEach(key => {
      if (headerFilters[key as keyof MainComponentFilters]) {
        initialFilters[key] = headerFilters[key as keyof MainComponentFilters] as string
      }
    })
    setLocalFilters(initialFilters)

    Object.keys(headerFilters).forEach(key => {
      if (filterInputRefs.current[key]) {
        filterInputRefs.current[key]!.value = (headerFilters[key as keyof MainComponentFilters] as string) || ''
      }
    })
  }, [headerFilters])

  const updateFilter = useCallback(
    (field: string, value: string | number | number[] | string[]) => {
      setLocalFilters(prev => {
        const updatedLocalFilters = {
          ...prev,
          [field]: Array.isArray(value) ? (value as string[] | number[]).join(',') : String(value || ''),
        }

        if (!value || (Array.isArray(value) && value.length === 0) || (typeof value === 'string' && !value.trim())) {
          if (field === 'type') {
            delete updatedLocalFilters['typeNameValue']
            delete updatedLocalFilters['typeNameLanguage']
          } else {
            delete updatedLocalFilters[field]
          }
        }

        const serverFilters: MainComponentFilters = {}

        Object.entries(updatedLocalFilters).forEach(([key, val]) => {
          const stringValue = String(val || '').trim()
          if (!stringValue) return

          if (key === 'status') {
            const statusValues = val
              .toString()
              .split(',')
              .map(Number)
              .filter(n => !isNaN(n))
            if (statusValues.length > 0) {
              serverFilters.status = statusValues
            }
          } else if (key === 'where') {
            const whereValues = val
              .toString()
              .split(',')
              .filter(s => s.trim())
            if (whereValues.length > 0) {
              serverFilters.where = whereValues
            }
          } else if (key === 'type') {
            serverFilters.typeNameValue = stringValue
            serverFilters.typeNameLanguage = i18n.language
          } else {
            serverFilters as MainComponentFilters
            serverFilters[key] = stringValue
          }
        })

        debouncedServerFilter(serverFilters)

        return updatedLocalFilters
      })
    },
    [debouncedServerFilter, i18n.language],
  )

  // Clear specific filter
  const clearFilter = useCallback(
    (field: string) => {
      if (filterInputRefs.current[field]) {
        filterInputRefs.current[field]!.value = ''
      }

      debouncedServerFilter.cancel()

      setLocalFilters(prev => {
        const newFilters = { ...prev }
        if (field == 'type') {
          delete newFilters['typeNameValue']
          delete newFilters['typeNameLanguage']
        } else {
          delete newFilters[field]
        }

        const serverFilters: MainComponentFilters = {}

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value && String(value).trim()) {
            if (key === 'type') {
              serverFilters.typeNameValue = value.trim()
              serverFilters.typeNameLanguage = i18n.language
            } else if (key === 'status') {
              const statusValues = Array.isArray(value)
                ? value
                : value
                  .split(',')
                  .map(Number)
                  .filter(n => !isNaN(n))
              if (statusValues.length > 0) {
                serverFilters.status = statusValues
              }
            } else if (key === 'where') {
              const whereValues = Array.isArray(value) ? value : value.split(',').filter(s => s.trim())
              if (whereValues.length > 0) {
                serverFilters.where = whereValues
              }
            } else {
              serverFilters as MainComponentFilters
              serverFilters[key] = value.trim()
            }
          }
        })

        onHeaderFiltersChange(serverFilters)

        return newFilters
      })
    },
    [i18n.language, onHeaderFiltersChange, debouncedServerFilter],
  )

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setLocalFilters({})
    Object.keys(filterInputRefs.current).forEach(field => {
      if (filterInputRefs.current[field]) {
        filterInputRefs.current[field]!.value = ''
      }
    })
    debouncedServerFilter.cancel()
    onHeaderFiltersChange({})
  }, [onHeaderFiltersChange, debouncedServerFilter])

  const createFilterHeader = useCallback(
    (
      headerName: string,
      field: string,
      filterType: 'text' | 'number' | 'date' | 'fulldate' | 'dropdown' = 'text',
      options?: {
        placeholder?: string
        dropdownOptions?: Array<{ value: string | number; label: string }>
        loading?: boolean
        multiple?: boolean
      },
    ) => {
      return (
        <FilterHeader
          headerName={headerName}
          field={field}
          filterType={filterType}
          placeholder={options?.placeholder}
          dropdownOptions={options?.dropdownOptions}
          loading={options?.loading}
          multiple={options?.multiple}
          filters={headerFilters}
          filterInputRefs={filterInputRefs}
          updateFilter={updateFilter}
          clearFilter={clearFilter}
        />
      )
    },
    [headerFilters, updateFilter, clearFilter, filterInputRefs, isLocationDataLoading],
  )

  const getLocationOptions = useMemo(() => {
    const options: Array<{ value: string | number; label: string }> = []

    if (localities) {
      localities?.forEach(locality => {
        options.push({
          value: `${locality.id}#${FacilityTypes.locality}`,
          label: locality.name,
        })
      })
    }

    if (serviceStations) {
      serviceStations.forEach(serviceStation => {
        options.push({
          value: `${serviceStation.id}#${FacilityTypes.serviceStation}`,
          label: serviceStation.name,
        })
      })
    }

    if (storages) {
      storages.forEach(storage => {
        options.push({
          value: `${storage.id}#${FacilityTypes.storage}`,
          label: storage.name,
        })
      })
    }

    return options
  }, [localities, serviceStations, storages])

  const STATIC_COLUMNS: GridColumns = useMemo(
    () => [
      {
        field: 'serialNumber',
        headerName: translate('serialNumber', { ns: 'main-component' }),
        flex: 1,
        minWidth: 130,
        renderHeader: () => createFilterHeader(translate('serialNumber', { ns: 'main-component' }), 'serialNumber'),
        renderCell: ({ row }) => <DynamicTextTypography text={row.serialNumber} maxLength={10} fontWeight={500} />,
      },
      {
        field: 'description',
        headerName: translate('description', { ns: 'common' }),
        flex: 1.5,
        minWidth: 160,
        renderHeader: () => createFilterHeader(translate('description', { ns: 'common' }), 'description'),
        renderCell: ({ row }) => <DynamicTextTypography text={row.description} maxLength={20} fontWeight={400} />,
      },
      {
        field: 'supplier',
        headerName: translate('Supplier', { ns: 'main-component' }),
        flex: 1,
        minWidth: 120,
        renderHeader: () => createFilterHeader(translate('Supplier', { ns: 'main-component' }), 'supplier'),
        renderCell: ({ row }) => <DynamicTextTypography text={row?.supplier?.name ?? '-'} maxLength={10} />,
      },
      {
        field: 'orderNumber',
        headerName: translate('orderNumber', { ns: 'main-component' }),
        flex: 0.75,
        minWidth: 110,
        renderHeader: () => createFilterHeader(translate('orderNumber', { ns: 'main-component' }), 'orderNumber'),
      },
      {
        field: 'dateProduced',
        headerName: translate('dateProduced', { ns: 'main-component' }),
        flex: 1,
        minWidth: 110,
        renderHeader: () =>
          createFilterHeader(translate('dateProduced', { ns: 'main-component' }), 'dateProduced', 'date'),
        renderCell: ({ row }) => {
          const producedYear = row?.productionYear != null ? row?.productionYear : '-'
          const producedMonth =
            row?.productionYear != null && row?.productionMonth != null
              ? '-' + MonthShortName[row?.productionMonth]
              : ''
          const dateProduced = producedYear + producedMonth
          return <DynamicTextTypography text={dateProduced} variant="body1" maxLength={10} fontWeight={400} />
        },
      },
      {
        field: 'status',
        headerName: translate('status', { ns: 'work-assignment-position' }),
        flex: 1,
        minWidth: 120,
        renderHeader: () =>
          createFilterHeader(translate('status', { ns: 'work-assignment-position' }), 'status', 'dropdown', {
            dropdownOptions: getStatusOptions(translate),
            multiple: true,
          }),
        renderCell: params => (
          <DynamicTextTypography
            text={translate(getMainComponentStatus(params.row), { ns: 'main-component' })}
            maxLength={10}
          />
        ),
      },
      {
        field: 'where',
        headerName: translate('where', { ns: 'main-component' }),
        flex: 1.2,
        minWidth: 140,
        renderHeader: () =>
          createFilterHeader(translate('where', { ns: 'main-component' }), 'where', 'dropdown', {
            dropdownOptions: getLocationOptions,
            loading: isLocationDataLoading,
            multiple: true,
          }),
        renderCell: renderMainComponentLocation,
      },
      {
        field: 'type',
        headerName: translate('type', { ns: 'common' }),
        flex: 0.75,
        minWidth: 100,
        renderHeader: () => createFilterHeader(translate('type', { ns: 'common' }), 'type'),
        sortComparator: (v1, v2) => {
          return getTranslatedText(v1).localeCompare(getTranslatedText(v2), undefined, {
            sensitivity: 'base',
          })
        },
        renderCell: ({ row }) => {
          return (
            <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={1}>
              <DynamicTextTypography text={getTranslatedText(row?.type.name)} maxLength={8} />
            </Box>
          )
        },
      },
      {
        field: 'circumference',
        headerName: translate('circumference', { ns: 'main-component' }),
        flex: 0.75,
        minWidth: 110,
        renderHeader: () => createFilterHeader(translate('circumference', { ns: 'main-component' }), 'circumference'),
        renderCell: params => {
          const circumference = params.row.circumference
          return <Typography fontWeight={400}>{circumference ? `${circumference}` : '-'}</Typography>
        },
      },
      {
        field: 'solidity',
        headerName: translate('solidity', { ns: 'main-component' }),
        flex: 0.75,
        minWidth: 110,
        renderHeader: () => createFilterHeader(translate('solidity', { ns: 'main-component' }), 'solidity'),
        renderCell: params => {
          const solidity = params.row.solidity
          return <Typography fontWeight={400}>{solidity ? `${solidity}` : '-'}</Typography>
        },
      },
      {
        field: 'pipeDiameter',
        headerName: translate('pipeDiameter', { ns: 'main-component' }),
        flex: 0.75,
        minWidth: 120,
        renderHeader: () => createFilterHeader(translate('pipeDiameter', { ns: 'main-component' }), 'pipeDiameter'),
        renderCell: params => {
          const customProperties = JSON.parse(params.row.customPropertyValues || '{}')
          return (
            <Typography fontWeight={400}>
              {customProperties.pipediameter ? `${customProperties.pipediameter}` : '-'}
            </Typography>
          )
        },
      },
      {
        field: 'perimeter',
        headerName: translate('perimeter', { ns: 'main-component' }),
        flex: 0.75,
        minWidth: 110,
        renderHeader: () => createFilterHeader(translate('perimeter', { ns: 'main-component' }), 'perimeter'),
        renderCell: params => {
          const customProperties = JSON.parse(params.row.customPropertyValues || '{}')
          return (
            <Typography fontWeight={400}>
              {customProperties.perimeter ? `${customProperties.perimeter}` : '-'}
            </Typography>
          )
        },
      },
      {
        field: 'lastWashedDate',
        headerName: translate('lastWashed', { ns: 'common' }),
        flex: 1,
        minWidth: 120,
        renderHeader: () => createFilterHeader(translate('lastWashed', { ns: 'common' }), 'lastWashedDate', 'fulldate'),
        renderCell: ({ value }) => (
          <Typography fontWeight={400}>{value ? String(value).split('T')[0] : '-'}</Typography>
        ),
      },
    ],
    [createFilterHeader, translate, renderMainComponentLocation],
  )

  const columns: GridColumns = useMemo(
    () => [
      ...STATIC_COLUMNS,
      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        headerName: translate('actions', { ns: 'common' }),
        width: 40,
        getActions: ({ id, row }) =>
          !row.isDeleted
            ? [
              <GridActionsCellItem
                key={id}
                label={translate('edit', { ns: 'common' })}
                icon={<MdEdit size={24} />}
                onClick={() => onUpdate(row as IMainComponent)}
                color="primary"
                showInMenu
              />,
              <GridActionsCellItem
                key={id}
                label={translate('clone', { ns: 'main-component' })}
                icon={<MdCopyAll size={24} />}
                onClick={() => onClone(row as IMainComponent)}
                color="primary"
                showInMenu
              />,
              <GridActionsCellItem
                key={id}
                label={translate('discard', { ns: 'common' })}
                icon={<MdDelete size={24} style={{ color: getThemeColor(theme, MUIThemeColors.errorMain) }} />}
                onClick={() => onDeleteMainComponent(row as IMainComponent)}
                disabled={
                  row.status === MainComponentStatus.Active ||
                  row.status === MainComponentStatus.Outgoing ||
                  row.status === MainComponentStatus.InLocalityStock ||
                  isDeleting ||
                  !hasPermission(
                    [permissionValue.ClientAdminPermission, permissionValue.ClientAdminPortal.Equipment_Portal],
                    permission,
                  )
                }
                sx={{
                  color: getThemeColor(theme, MUIThemeColors.errorMain),
                }}
                showInMenu
              />,
              <GridActionsCellItem
                key={id}
                label={translate('sendTo', { ns: 'main-component' })}
                icon={<MdSend size={24} />}
                onClick={() => onSend(id)}
                disabled={row.status != MainComponentStatus.NewlyRegistered}
                color="primary"
                showInMenu
              />,
              <GridActionsCellItem
                key={id}
                label={translate('sendToOutgoing', { ns: 'main-component' })}
                icon={<MdSend size={24} />}
                onClick={() => onSendToOutgoing(row as IMainComponent)}
                disabled={row.status != MainComponentStatus.NewlyRegistered}
                color="primary"
                showInMenu
              />,
            ]
            : [
              <GridActionsCellItem
                key={id}
                label={translate('bring-back', { ns: 'common' })}
                icon={<MdArrowBack size={24} />}
                onClick={() => bringBack(row)}
                color="primary"
                showInMenu
              />,
            ],
      },
    ],
    [
      STATIC_COLUMNS,
      translate,
      onUpdate,
      onClone,
      onDeleteMainComponent,
      isDeleting,
      permission,
      onSend,
      onSendToOutgoing,
      bringBack,
    ],
  )

  const handleOnClick = (mainComponent: IMainComponent) => {
    if (mainComponent.isDeleted) {
      toast.error(
        translate('discarded-equipment-error-message', {
          ns: 'main-component',
        }),
      )
      return
    }
    showDialog(
      EquipmentDetailModal,
      {
        componentProps: {
          customerId: customerId as EntityId,
          permission: permission,
          mainComponentId: mainComponent.id,
          mainComponentTypeId: mainComponent.type.id,
          isWithLayout: true,
          context: CustomerCtx,
        },
        opts: {
          maxWidth: 'xl',
          fullWidth: true,
          bgColor: getThemeColor(theme, MUIThemeColors.backgroundMain),
          rootWidth: '1400px',
        },
        title: formatMainComponentIdentification(mainComponent),
      },
      DialogContextType.customerAdminContext,
      true,
    )
  }

  const handleExcelDownload = async () => {
    await generateExcel(
      {
        reportId: SystemReportTypes.EquipmentReportByClient,
        customerId: customerId,
        filters: { search: searchText, ...headerFilters },
      },
      {
        onSuccess: data => {
          downloadFileLocal(
            data,
            translate('main-components', { ns: 'header' }) + '-' + new Date(Date.now()).toISOString(),
          )
        },
      },
    )
  }

  const handleBulkAction = useCallback(() => {
    if (selectedRows.length === 0) {
      toast.error(
        translate('selectAtLeastOneEquipment', { ns: 'main-component', defaultValue: 'Select at least one equipment' }),
      )
    }
  }, [selectedRows.length, translate])

  const enhancedToolbarOptions = useMemo(() => {
    const hasActiveFilters = Object.values(headerFilters).some(value => {
      if (Array.isArray(value)) {
        return value.length > 0
      }
      if (typeof value === 'string') {
        return value.trim().length > 0
      }
      return value !== null && value !== undefined
    })

    const baseOptions = [
      {
        label: '',
        Component: (
          <Box overflow={'hidden'}>
            <BulkActionHeaderActions
              customerId={customerId as EntityId}
              data={selectedRows}
              locality={localityForBulk}
            />
          </Box>
        ),
      },
      {
        label: translate('system-report-excel-download', { ns: 'document' }),
        Component: (
          <EquipmentToolbarStyledButton
            buttonIcon={
              <ExcelDownload height={'20px'} width={'15px'} color={getThemeColor(theme, MUIThemeColors.primaryMain)} />
            }
            loading={isGeneratingExcel}
            height="36px"
            width="36px"
            onClick={handleExcelDownload}
            variant="outlined"
          />
        ),
      },
    ]

    if (hasActiveFilters) {
      baseOptions.push({
        label: translate('clearFilters', { ns: 'common' }),
        Component: (
          <IconButton onClick={clearAllFilters} sx={{ mt: 1 }}>
            <Clear />
          </IconButton>
        ),
      })
    }

    return baseOptions
  }, [
    localFilters,
    clearAllFilters,
    handleExcelDownload,
    isGeneratingExcel,
    handleBulkAction,
    selectionModel,
    selectedRows,
  ])

  const CustomEmptyState = () => (
    <Box py={8} className="text-center">
      <EmptyStatusCard
        image={NoEquipment}
        header={translate('no-search-results-for-equipment-title', { ns: 'main-component' })}
        content={translate('no-search-results-for-equipment-content', { ns: 'main-component' })}
        imageWidth="162px"
        headerFontSize="18px"
        contentFontSize="14px"
      />
    </Box>
  )

  const queryParams = useMemo(() => {
    const params: Record<string, any> = {}

    if (searchText) {
      params.search = searchText
    }

    if (headerFilters) {
      Object.entries(headerFilters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (key === 'where' && Array.isArray(value)) {
            params.where = value.filter(v => v && v.trim())
          } else if (key === 'status' && Array.isArray(value)) {
            params.status = value
          } else if (Array.isArray(value)) {
            params[key] = value
          } else if (typeof value === 'string' && value.trim()) {
            params[key] = value.trim()
          } else if (typeof value === 'number') {
            params[key] = value
          }
        }
      })
    }

    return params
  }, [searchText, headerFilters])

  return (
    <StyledCard className="mb-2" height={window.innerHeight - 260 + 'px'}>
      <Box
        padding={1}
        sx={{
          '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root': {
            display: 'none !important',
          },
        }}
      >
        <StyledInfiniteScrollTable<IMainComponent>
          id={'main-components'}
          queryUrl={mainEndpoints.getMainComponentsBySearchBaseUrl(customerId as EntityId)}
          queryParams={queryParams}
          generateQueryKey={(page: number, pageSize: number) =>
            mainComponentQueryKeys.GET_BY_SEARCH_WITH_FILTERS(customerId, searchText, headerFilters, page, pageSize)
          }
          pageSize={rowCount}
          columns={columns}
          defaultColumnOption={columns.map(c => ({ field: c.field, headerName: c.headerName as string }))}
          enableColumnOptions={true}
          onRowClick={({ row }) => handleOnClick(row)}
          height={window.innerHeight - 330}
          getRowHeight={() => 44}
          enableToolbar={true}
          enableToolbarFilter={true}
          allowHorizontalScroll={true}
          checkboxSelection={true}
          disableSelectionOnClick
          selectionModel={selectionModel}
          onSelectionModelChange={newSelection => setSelectionModel(newSelection)}
          toolbarFilter={
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }} color={theme.palette.primary.main}>
                {translate('all', { ns: 'common' })} {translate('equipments', { ns: 'main-component' })}
                {Object.values(headerFilters).some(value => {
                  if (Array.isArray(value)) {
                    return value.length > 0
                  }
                  if (typeof value === 'string') {
                    return value.trim().length > 0
                  }
                  if (typeof value === 'number') {
                    return true
                  }
                  return value !== null && value !== undefined
                }) && <Typography component="span"> ({translate('filtered', { ns: 'common' })})</Typography>}
              </Typography>
              <FilterChips
                headerFilters={headerFilters}
                clearFilter={clearFilter}
                updateFilter={updateFilter}
                localities={localities ?? []}
                serviceStations={serviceStations}
                storages={storages}
              />
            </Box>
          }
          toolbarOptions={enhancedToolbarOptions}
          showHeaderOnEmptyState={true}
          noDataComponent={CustomEmptyState}
          onDataChange={handleDataChange}
          onTotalCountChange={handleTotalCountChange}
        />
      </Box>
    </StyledCard>
  )
}

export const FilterContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`

const tableFilterStyles = {
  '& .MuiDataGrid-virtualScroller': {
    marginTop: '56px !important',
  },
  '& .MuiDataGrid-columnHeader': {
    padding: '0 4px !important',
    position: 'relative',
    '& .MuiDataGrid-iconButtonContainer': {
      position: 'absolute !important',
      right: '4px !important',
      top: '20% !important',
      transform: 'translateY(-50%) !important',
      opacity: 0,
      transition: 'opacity 0.2s ease',
      visibility: 'hidden',
    },
    '&:hover .MuiDataGrid-iconButtonContainer': {
      opacity: 1,
      visibility: 'visible',
    },
  },
}

const infiniteTableFilterStyles = {
  ...tableFilterStyles,
  ...getFilterHeaderStyles(),
}

const StyledFilterGenericTable = styled(GenericTable)(() => infiniteTableFilterStyles)

export const StyledGenericTable = ((props: any) => (
  <StyledFilterGenericTable {...filterHeaderTableProps} {...props} />
)) as typeof GenericTable

const StyledFilterInfiniteScrollTable = styled(InfiniteScrollTable)(() => infiniteTableFilterStyles)

export const StyledInfiniteScrollTable = ((props: any) => (
  <StyledFilterInfiniteScrollTable {...filterHeaderTableProps} {...props} />
)) as typeof InfiniteScrollTable

export const EquipmentToolbarStyledButton = styled(GridActionButton)({
  marginBottom: '5px',
})

export default EquipmentListContainer
